import { trackDriveBrowserOpen } from '@/services/tracking'
import store from '@/store/root'
import { NavigationGuardNext, Route } from 'vue-router'
import {
  ensureUserLoggedIn,
  ensureUserLoggedInAndRedirectToPreviousPath,
  guardSelectPlan,
  guardSetup,
  redirectToLoginIfNotLoggedIn,
  redirectToOnboardingIfNeeded,
  restrictAccessToOwnersAndUsers,
  restrictAccessToOwnersAndUsersDrives,
} from './guards'

export default [
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('@/views/dashboard/index.vue'),
    meta: {
      layout: 'Platform',
      mainTab: true,
    },
    beforeEnter: redirectToOnboardingIfNeeded,
  },
  {
    path: '/computers',
    name: 'Computers',
    component: () => import('@/views/workstations/index.vue'),
    meta: {
      layout: 'Platform',
      mainTab: true,
    },
    beforeEnter: ensureUserLoggedIn,
  },
  {
    path: '/drives',
    name: 'Drives',
    component: () => import('@/views/drives/index.vue'),
    beforeEnter: ensureUserLoggedInAndRedirectToPreviousPath,
    meta: {
      layout: 'Platform',
      mainTab: true,
    },
  },

  {
    path: '/computers/assign/(.*)',
    name: 'AssignComputer',
    component: () => import('@/views/assign/OwnedResource.vue'),
    beforeEnter: ensureUserLoggedIn,
    meta: {
      layout: 'Default',
    },
    props: {
      resourceType: 'workstation',
    },
  },
  {
    path: '/drives/assign/(.*)',
    name: 'AssignDrive',
    component: () => import('@/views/assign/OwnedResource.vue'),
    beforeEnter: ensureUserLoggedIn,
    meta: {
      layout: 'Default',
    },
    props: {
      resourceType: 'drive',
    },
  },
  {
    path: '/drives/settings/(.*)',
    name: 'DriveSettings',
    component: () => import('@/views/drives/DriveSettings.vue'),
    beforeEnter: restrictAccessToOwnersAndUsersDrives, //TODO: add dedicated guard
    meta: {
      layout: 'Platform',
    },
    props: {
      resourceType: 'drive',
    },
  },
  {
    path: '/drives/(.*)',
    name: 'files',
    component: () => import('@/views/drives/Browse.vue'),
    beforeEnter: (to: Route, from: Route, next: NavigationGuardNext<Vue>) => {
      const profile = store.getters['user/profile']
      const uuid = to.params.pathMatch
      trackDriveBrowserOpen(profile, uuid)
      return ensureUserLoggedIn(to, from, next)
    },
    meta: {
      layout: 'Platform',
    },
  },
  {
    path: '/shared-links',
    name: 'SharedLinks',
    component: () => import('@/views/links/index.vue'),
    beforeEnter: ensureUserLoggedIn,
    meta: {
      layout: 'Platform',
    },
  },
  {
    path: '/teams',
    name: 'Teams',
    alias: '/team',
    component: () => import('@/views/team/index.vue'),
    beforeEnter: ensureUserLoggedIn,
    meta: {
      layout: 'Platform',
      mainTab: true,
    },
  },
  {
    path: '/invite',
    name: 'Invite',
    component: () => import('@/views/invite/index.vue'),
    beforeEnter: ensureUserLoggedIn,
    meta: {
      layout: 'Default',
    },
  },

  {
    path: '/activity',
    name: 'Activity',
    component: () => import('@/views/logs/DashboardUserActivity.vue'),
    beforeEnter: ensureUserLoggedIn,
    meta: {
      layout: 'Platform',
      mainTab: true,
    },
  },
  {
    path: '/settings(.*)',
    name: 'Settings',
    component: () => import('@/views/settings/index.vue'),
    meta: {
      layout: 'Platform',
    },
    beforeEnter: restrictAccessToOwnersAndUsers,
  },
  {
    path: '/account(.*)',
    name: 'account',
    component: () => import('@/views/account/index.vue'),
    meta: {
      layout: 'Platform',
    },
    beforeEnter: ensureUserLoggedIn,
  },
  {
    path: '/select-plan',
    name: 'selectPlan',
    component: () => import('@/views/account/SelectPlan.vue'),
    beforeEnter: guardSelectPlan,
    meta: {
      layout: 'Default',
    },
  },
  {
    path: '/bug',
    name: 'BugReport',
    component: () => import('@/views/support/Bug.vue'),
    meta: {
      layout: 'Default',
    },
    beforeEnter: ensureUserLoggedIn,
  },
  {
    path: '/admin',
    name: 'admin',
    component: () => import('@/views/admin/Admin.vue'),
    meta: {
      layout: 'Platform',
    },
    beforeEnter: ensureUserLoggedIn,
  },
  {
    path: '/admin/activity/:uid',
    name: 'adminUserActivity',
    component: () => import('@/views/admin/AdminUserActivity.vue'),
    meta: {
      layout: 'Platform',
    },
    beforeEnter: ensureUserLoggedIn,
  },

  {
    path: '/transcoded',
    name: 'TranscodingList',
    component: () => import('@/views/transcoded/index.vue'),
    meta: {
      layout: 'Platform',
    },
    beforeEnter: ensureUserLoggedIn,
  },

  {
    path: '/welcome',
    name: 'welcomeToSetup',
    beforeEnter: guardSetup,
    meta: {
      layout: 'Welcome',
    },
  },

  {
    path: '/setup',
    name: 'Setup',
    component: () => import('@/views/setup/index.vue'),
    beforeEnter: guardSetup,
  },
  {
    path: '/missing-payment',
    name: 'MissingPayment',
    component: () => import('@/views/account/MissingPayment.vue'),
    meta: {
      layout: 'Default',
      shouldHideGoBack: true,
    },
    beforeEnter: redirectToLoginIfNotLoggedIn,
  },
  {
    path: '/no-credits',
    name: 'NoCredits',
    component: () => import('@/views/account/NoCredits.vue'),
    meta: {
      layout: 'Default',
      shouldHideGoBack: true,
    },
    beforeEnter: redirectToLoginIfNotLoggedIn,
  },
  {
    path: '/add-credits',
    name: 'AddCredits',
    component: () => import('@/views/account/AddCredits.vue'),
    meta: {
      layout: 'Platform',
    },
    beforeEnter: redirectToLoginIfNotLoggedIn,
  },
]
